import { useEffect, useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const EMPTY = { name: "", slug: "", description: "", image: "", type: "manual", product_ids: [], rules: [], featured: false };

export default function Collections() {
  const [collections, setCollections] = useState([]);
  const [products, setProducts] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);

  const load = () => {
    api.get("/admin/collections").then((r) => setCollections(r.data)).catch((e) => toast.error(errMsg(e)));
    api.get("/admin/products").then((r) => setProducts(r.data)).catch(() => {});
  };
  useEffect(load, []);

  const save = async () => {
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    try {
      if (editId) await api.put(`/admin/collections/${editId}`, form);
      else await api.post("/admin/collections", form);
      toast.success("Collection saved");
      setOpen(false);
      setForm(EMPTY);
      setEditId(null);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this collection?")) return;
    await api.delete(`/admin/collections/${id}`);
    load();
  };

  const toggleProduct = (pid) =>
    setForm((f) => ({ ...f, product_ids: f.product_ids.includes(pid) ? f.product_ids.filter((x) => x !== pid) : [...f.product_ids, pid] }));

  return (
    <div className="space-y-4" data-testid="admin-collections-page">
      <div className="flex items-center justify-between">
        <h1 className="font-heading text-2xl font-bold tracking-tight">Collections</h1>
        <Button size="sm" className="bg-neutral-900 hover:bg-brand text-white btn-press" onClick={() => { setForm(EMPTY); setEditId(null); setOpen(true); }} data-testid="new-collection-btn">
          <Plus size={15} className="mr-1" /> New Collection
        </Button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {collections.map((c) => (
          <div key={c.id} className="bg-white border border-neutral-200 p-4" data-testid={`collection-card-${c.slug}`}>
            <div className="flex items-start justify-between">
              <div>
                <p className="font-heading font-semibold">{c.name}</p>
                <p className="text-xs font-mono text-neutral-400 mt-0.5">/{c.slug}</p>
              </div>
              <span className={`text-[10px] font-mono uppercase px-2 py-0.5 ${c.type === "rule" ? "bg-violet-100 text-violet-800" : "bg-neutral-100 text-neutral-600"}`}>
                {c.type === "rule" ? "Rule-based" : "Manual"}
              </span>
            </div>
            <p className="text-sm text-neutral-500 mt-2 line-clamp-2">{c.description}</p>
            <div className="flex gap-2 mt-4">
              <Button size="sm" variant="outline" onClick={() => { setForm({ ...EMPTY, ...c }); setEditId(c.id); setOpen(true); }} data-testid={`edit-collection-${c.slug}`}>Edit</Button>
              <Button size="sm" variant="outline" className="text-red-600" onClick={() => del(c.id)} data-testid={`delete-collection-${c.slug}`}><Trash2 size={14} /></Button>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto" aria-describedby={undefined} data-testid="collection-dialog">
          <DialogHeader><DialogTitle className="font-heading">{editId ? "Edit" : "New"} Collection</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="col-name-input" /></div>
            <div><Label>Slug</Label><Input value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} placeholder="auto if empty" data-testid="col-slug-input" /></div>
            <div className="col-span-2"><Label>Description</Label><Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="col-desc-input" /></div>
            <div className="col-span-2"><Label>Image URL</Label><Input value={form.image} onChange={(e) => setForm({ ...form, image: e.target.value })} data-testid="col-image-input" /></div>
            <div>
              <Label>Type</Label>
              <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                <SelectTrigger data-testid="col-type-select"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="manual">Manual</SelectItem><SelectItem value="rule">Rule-based</SelectItem></SelectContent>
              </Select>
            </div>
            <div className="flex items-end gap-2 pb-1">
              <Switch checked={form.featured} onCheckedChange={(v) => setForm({ ...form, featured: v })} data-testid="col-featured-switch" />
              <Label>Featured on home</Label>
            </div>
          </div>

          {form.type === "manual" ? (
            <div className="mt-4">
              <Label>Products ({form.product_ids.length} selected)</Label>
              <div className="border border-neutral-200 mt-2 max-h-56 overflow-y-auto p-2 grid grid-cols-1 sm:grid-cols-2 gap-1">
                {products.map((p) => (
                  <label key={p.id} className="flex items-center gap-2 text-sm p-1.5 hover:bg-neutral-50 cursor-pointer">
                    <input type="checkbox" checked={form.product_ids.includes(p.id)} onChange={() => toggleProduct(p.id)} data-testid={`col-product-${p.slug}`} />
                    <span className="truncate">{p.title}</span>
                  </label>
                ))}
              </div>
            </div>
          ) : (
            <div className="mt-4">
              <div className="flex items-center justify-between">
                <Label>Rules (all must match)</Label>
                <Button size="sm" variant="outline" onClick={() => setForm({ ...form, rules: [...form.rules, { field: "category", op: "eq", value: "" }] })} data-testid="col-add-rule">
                  <Plus size={13} className="mr-1" /> Add Rule
                </Button>
              </div>
              {form.rules.map((r, i) => (
                <div key={i} className="flex gap-2 mt-2 items-center" data-testid={`col-rule-${i}`}>
                  <Select value={r.field} onValueChange={(v) => setForm({ ...form, rules: form.rules.map((x, j) => (j === i ? { ...x, field: v } : x)) })}>
                    <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="category">Category</SelectItem>
                      <SelectItem value="tag">Tag</SelectItem>
                      <SelectItem value="price">Price</SelectItem>
                      <SelectItem value="title">Title</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select value={r.op} onValueChange={(v) => setForm({ ...form, rules: form.rules.map((x, j) => (j === i ? { ...x, op: v } : x)) })}>
                    <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="eq">equals</SelectItem>
                      <SelectItem value="contains">contains</SelectItem>
                      <SelectItem value="lt">&lt; than</SelectItem>
                      <SelectItem value="gt">&gt; than</SelectItem>
                    </SelectContent>
                  </Select>
                  <Input value={r.value} onChange={(e) => setForm({ ...form, rules: form.rules.map((x, j) => (j === i ? { ...x, value: e.target.value } : x)) })} placeholder="Value" />
                  <Button variant="ghost" size="icon" onClick={() => setForm({ ...form, rules: form.rules.filter((_, j) => j !== i) })}><Trash2 size={14} /></Button>
                </div>
              ))}
            </div>
          )}
          <Button onClick={save} className="w-full mt-4 bg-neutral-900 hover:bg-brand text-white btn-press" data-testid="col-save-btn">Save Collection</Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
